import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getSessionId,
  getSessionIdFromUrl,
  pathWithSessionId,
  initializeSession,
} from '../utils/sessionManager';
import { getApiBaseUrl } from '../utils/apiConfig';
import { saveSkinDetection } from '../utils/dataSaver';
import { 
  detectHair 
} from '../utils/hairAnalysisApi';
import {
  normalizeDetections,
  normalizeSkinScore,
} from '../utils/skinConditionNormalization';

import PageFooter from '../components/PageFooter';
import { useHairSession } from '../context/HairSessionContext';
import { navigateHairFlowHome } from '../utils/hairSalonFlowDeps';
import './AIHairAnalysisPage.css';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';

function HomeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function useResolvedSession(location, token) {
  const [sessionId, setSessionId] = useState(() => {
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    return fromState ?? fromUrl ?? fromStorage ?? null;
  });
  const apiBaseUrl = getApiBaseUrl();

  useEffect(() => {
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    const resolved = fromState || fromUrl || fromStorage;

    if (resolved && sessionId !== resolved) {
      setSessionId(resolved);
      return;
    }
    if (sessionId) return;

    const init = async () => {
      const sid = await initializeSession(apiBaseUrl, token ? { app_session_token: token } : {});
      setSessionId(sid);
    };
    init();
  }, [apiBaseUrl, sessionId, location, token]);

  return [sessionId, setSessionId];
}

const AIHairAnalysisPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useHairSession();

  const hairCaptures = location.state?.hairCaptures ?? null;
  const magnifiedScalp = location.state?.magnifiedScalp ?? null;
  const userDataFromState = location.state?.userData ?? null;
  const aqiDataFromState = location.state?.aqiData ?? null;

  const [sessionId, setSessionId] = useResolvedSession(location, token);
  const [isDetecting, setIsDetecting] = useState(false);
  const [status, setStatus] = useState("Preparing...");
  const [error, setError] = useState(null);

  const hasDetectedRef = useRef(false);
  const detectionInProgressRef = useRef(false);

  const navigateAfterAnalysis = useCallback(
    (hairResults, finalSessionId, aiConditionDescriptions = null, previousHairData = null, dbRecommendations = null) => {
      const baseState = {
        hairCaptures,
        userData: userDataFromState,
        sessionId: finalSessionId,
        scanId: hairResults.scan_id ?? hairResults.scanId ?? null,
        hairResults,
        aqiData: aqiDataFromState,
        aiConditionDescriptions: aiConditionDescriptions ?? null,
        previousHairData: previousHairData ?? null,
        ...(dbRecommendations && Object.keys(dbRecommendations).length > 0 ? { dbRecommendations } : {}),
        ...(token && { token }),
      };

      if (!token) {
        navigate(pathWithSessionId('/hair-report', finalSessionId), {
          state: baseState,
          replace: true,
        });
        return;
      }

      const run = async () => {
        // For hair analysis, we go directly to results
        navigate(pathWithSessionId('/hair-report', finalSessionId), {
          state: baseState,
          replace: true,
        });
      };

      void run();
    },
    [navigate, hairCaptures, userDataFromState, aqiDataFromState, token]
  );

  const runDetection = useCallback(
    async (imageDataUrl) => {
      if (
        !imageDataUrl ||
        isDetecting ||
        !sessionId ||
        hasDetectedRef.current ||
        detectionInProgressRef.current
      ) {
        return;
      }

      hasDetectedRef.current = true;
      detectionInProgressRef.current = true;
      const currentSessionId =
        sessionId ?? location.state?.sessionId ?? getSessionId();
      const userAge = userDataFromState?.age ?? null;

      console.log("DEBUG: runDetection called with sessionId:", currentSessionId);
      setIsDetecting(true);
      setStatus("Initializing analysis...");
      setError(null);
      let data;
      try {
        setStatus("Analyzing your hair...");
        console.log("DEBUG: Calling multi-view detectHair...");
        data = await detectHair({
          hairCaptures: {
            ...hairCaptures,
            ...(magnifiedScalp ? { magnified_scalp: magnifiedScalp } : {}),
          },
          hairType: userDataFromState?.hairType || 'Straight',
          sessionId: currentSessionId,
          age: userAge,
          appSessionToken: token || null,
          name: userDataFromState?.name ?? null,
          mobileNumber: userDataFromState?.mobileNumber ?? userDataFromState?.phone ?? null,
          email: userDataFromState?.email ?? null,
        });
        console.log("DEBUG: detectHair response:", data);
        setStatus("Processing results...");
      } catch (err) {
        console.error("DEBUG: detectHair error:", err);
        const isTimeout = err?.name === 'TimeoutError' || /timed out/i.test(err?.message || '');
        const isBusy = err?.status === 429;
        const friendly = isBusy
          ? 'Analysis is already running. Please wait a moment, then tap Retry.'
          : isTimeout
          ? 'The analysis took too long. Close other apps to free RAM, then tap Retry.'
          : (err?.message || 'Failed to analyze hair conditions');
        setStatus("Error: " + friendly);
        // Keep hasDetectedRef true so useEffect does not auto-retry (that stacks Ollama jobs).
        hasDetectedRef.current = true;
        detectionInProgressRef.current = false;
        setError(friendly);
        setIsDetecting(false);
        return;
      }

      if (!data.success) {
        hasDetectedRef.current = true;
        detectionInProgressRef.current = false;
        setError(data.error || 'Detection failed');
        setIsDetecting(false);
        return;
      }

      const detectionsList = normalizeDetections(data.detections ?? []);
      const finalHairConditions = data.hair_conditions ?? null;
      const finalHairScore = normalizeSkinScore(data.hair_score ?? null);
      const finalSessionId = data.session_id || currentSessionId;

      if (data.session_id) {
        setSessionId(data.session_id);
      }

      saveSkinDetection(
        finalSessionId,
        {
          detections: detectionsList,
          hairScore: finalHairScore,
          hairConditions: finalHairConditions,
          markedImage: data.marked_image,
          positionResults: data.position_results,
          scanId: data.scan_id ?? null,
        },
        userDataFromState ?? {},
        token ?? null
      ).catch(() => {});

      navigateAfterAnalysis(
        {
          ...data,
          detections: detectionsList,
          hairScore: finalHairScore,
          hairConditions: finalHairConditions,
        },
        finalSessionId,
        {},
        null,
        null
      );
    },
    [
      isDetecting,
      sessionId,
      setSessionId,
      location.state?.sessionId,
      userDataFromState,
      token,
      hairCaptures,
      magnifiedScalp,
      navigateAfterAnalysis,
    ]
  );

  useEffect(() => {
    if (
      hairCaptures &&
      !isDetecting &&
      sessionId &&
      !hasDetectedRef.current &&
      !detectionInProgressRef.current
    ) {
      runDetection(hairCaptures);
    }
  }, [hairCaptures, sessionId, runDetection, isDetecting]);

  const handleBack = useCallback(() => {
    const sid = sessionId ?? getSessionIdFromUrl(location) ?? getSessionId();
    const path = pathWithSessionId('/capture', sid);
    navigate(path, {
      state: {
        hairCaptures,
        userData: userDataFromState,
        sessionId: sessionId,
        aqiData: aqiDataFromState,
        ...(token && { token }),
      },
    });
  }, [navigate, sessionId, location, hairCaptures, userDataFromState, aqiDataFromState, token]);

  const handleHome = useCallback(() => {
    navigateHairFlowHome(navigate, sessionLandingPath);
  }, [navigate, sessionLandingPath]);

  const handleRetry = useCallback(() => {
    hasDetectedRef.current = false;
    detectionInProgressRef.current = false;
    runDetection(hairCaptures);
  }, [runDetection, hairCaptures]);

  if (!hairCaptures) {
    return (
      <div className="ai-hair-analysis-page page-bg">
        <header className="ai-hair-analysis-page-nav" role="banner">
          <button
            type="button"
            className="ai-hair-analysis-page-back-button"
            onClick={handleBack}
            aria-label="Go back"
          >
            ←
          </button>
          <button
            type="button"
            className="ai-hair-analysis-page-home-button"
            onClick={handleHome}
            aria-label="Go to home"
          >
            <HomeIcon />
          </button>
        </header>
        <main className="ai-hair-analysis-page-main">
          <div className="error-container">
            <h2>No Image Found</h2>
            <p>Please capture an image first.</p>
            <button type="button" className="skin-action-button" onClick={handleBack}>
              Go Back
            </button>
          </div>
        </main>
        <PageFooter />
      </div>
    );
  }

  return (
    <div className="ai-hair-analysis-page page-bg">
      <header className="ai-hair-analysis-page-nav" role="banner">
        <button
          type="button"
          className="ai-hair-analysis-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="ai-hair-analysis-page-title-wrapper">
          <h1 className="ai-hair-analysis-page-title">Analyzing Your Hair</h1>
          <button
            type="button"
            className="ai-hair-analysis-page-home-button"
            onClick={handleHome}
            aria-label="Go to home"
          >
            <HomeIcon />
          </button>
        </div>
      </header>
      <main className="ai-hair-analysis-page-main">
        {error ? (
          <div className="error-container">
            <h2>Analysis Interrupted</h2>
            <p>{error}</p>
            <button type="button" className="skin-action-button" onClick={handleRetry}>
              Retry Analysis
            </button>
          </div>
        ) : (
          <div className="loading-container">
            <div className="analysis-loader-spinner"></div>
            <div className="ai-hair-analysis-status-premium">
              <div className="status-badge">
                <span className="pulse-dot"></span>
                {status}
              </div>
            </div>
            <p className="analysis-subtitle">
              Our AI is performing a high-precision surface scan...
            </p>
          </div>
        )}
      </main>
      <PageFooter />
    </div>
  );
};

export default AIHairAnalysisPage;
